
import { Workbook, Worksheet } from 'exceljs'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { ErrorImport, ImportErrorCode } from '@/types/Errors'
import { ErrorTitles, ImportationErrorAdvices } from '@/types/ErrorDictionary'
import LogService, { ToString } from '@/services/support/LogService'

// eslint-disable-next-line
enum ImportationType {
  // eslint-disable-next-line
  CREATION = 'creation',
  // eslint-disable-next-line
  UPDATE = 'update'
}

@Component
export default class ImportFile extends Vue {
  @Prop({ required: false, default: 'Importar' })
  actionName: string

  @Prop({ required: true })
  entityName: string

  @Prop({ required: true })
  fields: any[]

  @Prop({ required: true })
  entityFields: any

  @Prop({ required: true })
  prepareEntity: any

  @Prop({ required: true })
  saveEntity: any

  @Prop({ required: false })
  updateEntity: any

  @Prop({ required: false, default: 'd-lg-block' })
  sizeClass: string

  file: File = null as any
  fileName: string = ''
  sheetName: string = ''
  importFields: any[] = []
  importData: any = {
    headers: [],
    records: []
  }
  importationTypes = ImportationType
  processErrors: ErrorImport[] = []
  preProcessingFile: boolean = false
  preProcessedRecods: any[] = []
  showRecomendedErrorActions: boolean = false
  uploaded: any[] = []
  currentPage = 1
  perPage = 12
  loadingImport = false
  importType = ImportationType.CREATION

  mounted () {
    this.importFields = []
    this.importFields = this.fields
    // Add 'status' and 'details' columns to display in modal resume
    if (!this.importFields.some((element: any) => element.key === 'status')) {
      this.importFields.push({ key: 'status', label: 'Estado', sortable: true })
    }
    if (!this.importFields.some((element: any) => element.key === 'details')) {
      this.importFields.push({ key: 'details', label: 'Detalle' })
    }
    if (!this.importFields.some((element: any) => element.key === 'request_id')) {
      this.importFields.push({ key: 'request_id', label: 'Identificador' })
    }
  }

  /**
   * Validates if the current importation type
   * is a determinate type
   *
   * @param type to compare
   */
  isImportationType (type: ImportationType): boolean {
    return this.importType === type
  }

  get forCreation () {
    return this.file && !this.loadingImport && this.isImportationType(ImportationType.CREATION)
  }

  get forUpdate () {
    return this.file && !this.loadingImport && this.isImportationType(ImportationType.UPDATE)
  }

  /**
   * Creates modal identifier name based on entityName property
   * @returns modal name to identify diferent modals
   */
  get modalId () {
    return 'modal-import-' + this.entityName
  }

  /**
   * Create title to modal component
   * @returns string title
   */
  get modalTitle () {
    return 'Importar archivo para ' + this.entityName
  }

  /**
   * Clear modal values and close it
   */
  closeModal () {
    // Emit event to reload entity records
    if (this.uploaded.length) {
      this.$emit('reload-data')
    }
    this.resetModal()
    this.$bvModal.hide(this.modalId)
  }

  /**
   * Clear modal values
   */
  resetModal () {
    this.file = null as any
    this.fileName = ''
    this.sheetName = ''
    this.uploaded = []
    this.processErrors = []
    this.preProcessedRecods = []
    this.importData = {
      headers: [],
      records: []
    }
  }

  /**
   * Read XLSX file selected
   * @param event - onchange event
   */
  handleFile (event: any) {
    this.resetModal()
    let files = event.target.files
    let file = files[0]
    if (file) {
      let reader = new FileReader()
      reader.onloadstart = () => {
        this.loadingImport = true
        // Validates the file extension
        let fileExtension = file.name.split('.').pop().toLowerCase()
        if (fileExtension !== 'xlsx') {
          this.processErrors.push(
            new ErrorImport(
              ImportErrorCode.NO_FILE_READED,
              ErrorTitles.no_file_readed,
              'El formato del archivo no es válido. Solo los archivos XLSX son compatibles para importación de registros.',
              ImportationErrorAdvices.no_file_readed
            ))
          reader.abort()
        }
      }
      reader.onload = (e: any) => {
        let data = new Uint8Array(e.target.result)
        const workbook = new Workbook()
        workbook.xlsx.load(data).then(() => {
          const workSheet = workbook.worksheets[0]
          const result = this.getDataImport(workSheet)
          this.sheetName = workSheet.name
          this.importData.headers = result.headers
          this.importData.records = result.records
          this.loadingImport = false
          this.preImportFile()
        })
      }
      reader.onerror = (error) => {
        LogService.error(`Error al leer el archivo: ${ToString(error)}`)
        this.loadingImport = false
        this.processErrors.push(
          new ErrorImport(
            ImportErrorCode.NO_FILE_READED,
            ErrorTitles.no_file_readed,
            'Ocurrió un error al leer el archivo que intenta importar',
            ImportationErrorAdvices.no_file_readed
          ))
      }

      reader.onabort = () => {
        LogService.error(`Error al leer el archivo`)
        this.loadingImport = false
      }

      reader.readAsArrayBuffer(file)
    }
  }

  /**
   * Makes a pre-entity to validates that each record
   * to send has the required values and with a valid format.
   */
  async preImportFile () {
    this.preProcessingFile = true
    try {
      if (this.importType === ImportationType.CREATION) {
        await this.preImportFileCreation()
      } else if (this.importType === ImportationType.UPDATE) {
        await this.preImportFileUpdate()
      }
      this.preProcessingFile = false
    } catch (error) {
      LogService.error(`Error en pre-importación de registros. Error no manejado: ${ToString(error)}`)
    }
  }

  /**
   * Generates the row data and headers
   */
  getDataImport (worksheet: Worksheet | undefined): { headers: any[], records: any[] } {
    let headers: any[] = []
    const records: any[] = []
    worksheet?.eachRow({ includeEmpty: false }, (row, rowIndex) => {
      const rowData: any = {}
      if (rowIndex === 1) {
        row.eachCell({ includeEmpty: true }, (cell, cellIndex) => {
          headers.push(cell.value ? cell.value.toString() : `no_header${cellIndex}`)
        })
      } else {
        row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
          rowData[`${headers[colNumber - 1]}`] = cell.value
        })
        records.push(rowData)
      }
    })
    return { headers, records }
  }
  /**
   * Tries to prepare all entities readed
   * and acumulates its posible errors
   */
  async preImportFileCreation () {
    for (let entity of this.importData.records) {
      await this.prepareEntity(entity)
        .catch((error: any) => {
          if (error instanceof ErrorImport) {
            if (error.code === ImportErrorCode.NO_REQUIRED_VALUES &&
              this.processErrors.findIndex(e => e.code === error.code) === -1) {
              this.processErrors.push(error)
            } else if (error.code !== ImportErrorCode.NO_REQUIRED_VALUES) {
              this.processErrors.push(error)
            }
          }
        })
    }
  }

  /**
   * Tries to update all entities readed
   * and acumulates its posible errors
   */
  async preImportFileUpdate () {
    if (!this.importData.headers.includes('id')) {
      this.processErrors.push(ErrorImport.getIDColumnError(
        [
          'Revisar que el formato seleccionado es el correcto',
          'Verificar que el formato cuente con una columna "id"',
          'Recuerda que para la actualización de registros se necesita saber cuál registro será actualizado'
        ])
      )
    } else {
      await this.prepareUpdateEntities(0, 10)
    }
  }

  /**
   * Prepares the entities that want to update their values in
   * as many batches as necessary
   *
   * @param startIndex - to indicate the start index batch
   * @param batchSize - number of promises to process in a batch
   */
  async prepareUpdateEntities (startIndex: number, batchSize: number) {
    let promises: Promise<any>[] = []
    this.preProcessingFile = true
    let highIndex = startIndex
    for (let i = startIndex; i < this.importData.records.length && i <= startIndex + batchSize; i++, highIndex++) {
      let entity = this.importData.records[i]
      let updatePromise = this.updateEntity(entity)
        .then((res: any) => {
          this.preProcessedRecods.push(res)
        })
        .catch((error: any) => {
          if (error instanceof ErrorImport) {
            this.processErrors.push(error)
          } else {
            LogService.error(`Error en pre-actualización de registros. Error no manejado: ${ToString(error)}`)
          }
        })
      promises.push(updatePromise)
    }

    // Awaits until all promises has been resolved or rejected to send a new promises batch
    await Promise.allSettled(promises).then(() => {
      if (highIndex >= this.importData.records.length) {
        this.preProcessingFile = false
        return
      }
      this.prepareUpdateEntities(highIndex, batchSize)
    })
  }

  /**
   * Create all entity records from 'importData.record' values
   * previusly loaded
   */
  async importFile (index: number) {
    this.fileName = this.file.name
    let entityPromises: Promise<any>[] = []
    this.loadingImport = true
    let indexHigh = index
    let batchSize = 25
    // Creates as many entity promises than records to import or batch size range
    for (let i = index; i < this.importData.records.length && i <= index + batchSize; i++) {
      try {
        indexHigh += 1
        // Handles the importation for creation processes
        let currentEntity = await this.prepareEntity(this.importData.records[i])
        let entityPromise = this.saveEntity(currentEntity)
        let entityFields = this.entityFields(currentEntity)
        entityPromises.push(entityPromise)
        entityPromise
          .then((response: any) => {
            // Set 'status' and 'details' with success values to display in modal resume
            entityFields.id = response.id
            entityFields = this.updateEntityFields(
              entityFields, 'Ok', 'Actualizado correctamente',
              response['header-request-id'],
              { status: 'success', request_id: 'success' }
            )
            this.uploaded.push(entityFields)
          })
          .catch((error: any) => {
            // Manage current and future error format
            let errors = this.getErrorList(error)
            let requestId = error.response.headers['request-id']
            // Set 'status' and 'details' with error values to display in modal resume
            entityFields = this.updateEntityFields(
              entityFields, 'Error', errors.join(','), requestId,
              { status: 'danger', request_id: 'danger' }
            )
            this.uploaded.push(entityFields)
          })
      } catch (error) {
        LogService.error(`Error durante envío de peticiones: ${ToString(error)}`)
      }
    }

    // Awaits until all client promises has been resolved or rejected to send a new promises batch
    Promise.allSettled(entityPromises).then(() => {
      if (indexHigh >= this.importData.records.length) {
        this.fileProcessSuccess(this.fileName)
        this.file = null as any
        this.loadingImport = false
        return
      }
      this.importFile(indexHigh)
    })
  }

  /**
   * Updates all entity previusly loaded and validated
   */
  async importFileToUpdate (index: number) {
    this.fileName = this.file.name
    let entityPromises: Promise<any>[] = []
    this.loadingImport = true
    let indexHigh = index
    let batchSize = 25
    // Creates as many entity promises than records to import or batch size range
    for (let i = index; i < this.preProcessedRecods.length && i <= index + batchSize; i++, indexHigh++) {
      try {
        let currentEntity = this.preProcessedRecods[i]
        let updateEntityPromise = this.saveEntity(currentEntity)
        let entityFields = this.entityFields(currentEntity)
        entityPromises.push(updateEntityPromise)
        updateEntityPromise
          .then((response: any) => {
            // Set 'status' and 'details' with success values to display in modal resume
            entityFields = this.updateEntityFields(
              entityFields, 'Ok', 'Actualizado correctamente',
              response['header-request-id'],
              { status: 'success', request_id: 'success' }
            )
            this.uploaded.push(entityFields)
          })
          .catch((error: any) => {
            // Manage current and future error format
            let errors = this.getErrorList(error)
            let requestId = error.response.headers['request-id']
            // Set 'status' and 'details' with error values to display in modal resume
            entityFields = this.updateEntityFields(
              entityFields, 'Error', errors.join(','), requestId,
              { status: 'danger', request_id: 'danger' }
            )
            this.uploaded.push(entityFields)
          })
      } catch (error) {
        LogService.error(`Error durante envío de peticiones por actualización: ${ToString(error)}`)
      }
    }

    // Awaits until all promises has been resolved or rejected to send a new promises batch
    Promise.allSettled(entityPromises).then(() => {
      if (indexHigh >= this.preProcessedRecods.length) {
        this.fileProcessSuccess(this.fileName)
        this.file = null as any
        this.loadingImport = false
        return
      }
      this.importFileToUpdate(indexHigh)
    })
  }

  /**
   * Sets the values for the columns to display as results for the entity importation
   *
   * @param columns - to set values
   * @param status - to set as promise status value
   * @param details - to set as a message information by the promise state
   * @param requestId - to set in the column request-id as trace by the request
   * @param cellVariant - color format for the request-id column
   */
  updateEntityFields (columns: any, status: string, details: string, requestId: any, cellVariant: any) {
    columns['status'] = status
    columns['details'] = details
    columns['request_id'] = requestId
    columns['_cellVariants'] = cellVariant
    return columns
  }

  /**
   * @returns a list of error details by the API user response
   */
  getErrorList (error: any) {
    let dataError = error.response.data
    let errors = []
    if (dataError.errors) {
      dataError.errors.forEach((e: any) => {
        errors.push(e.description)
      })
    } else {
      errors.push(dataError.description ? dataError.description : error.response)
    }
    return errors
  }

  /**
   * Launches a confirmation message of the end of the file import process
   * @param fileName that was processed
   */
  fileProcessSuccess (fileName: string) {
    this.$bvToast.toast('El archivo "' + fileName + '" ha sido procesado por completo', {
      title: 'Archivo procesado',
      variant: 'success',
      toaster: 'b-toaster-top-center',
      solid: true,
      autoHideDelay: 5000
    })
  }

  /**
   * Counts records uploaded
   */
  get uploadedCount () {
    return this.uploaded.filter((elem) => elem.status === 'Ok').length
  }

  /**
   * Counts records not uploaded
   */
  get notUploadedCount () {
    return this.uploaded.filter((elem) => elem.status !== 'Ok').length
  }

  /**
   * Counts all records uploaded succeed or rejected
   */
  get totalUploaded () {
    return this.uploaded.length
  }

  /**
   * Download results from a imported file in a csv format
   */
  exportResults () {
    let csv = ''
    // Add columns values
    let columnFields = Object.values(this.importFields).map((field) => { return field.label })
    csv += columnFields.join(',')
    csv += '\n'

    // Process each uploaded element
    this.uploaded.forEach((element: any) => {
      let values = Object.values(element).filter((elem) => typeof elem !== 'object')
      let clearValues = values.map((elem: any) => {
        if (typeof elem === 'number') {
          elem = elem.toString()
        }
        if (elem.search(/\n|\r/gi) !== -1) {
          elem = elem.replace(/\n|\r/gi, '')
        }
        return elem
      })
      csv += clearValues.join(',')
      csv += '\n'
    })
    let element = document.createElement('a')
    element.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    element.target = '_blank'
    element.download = 'Resultados_de_importación.csv'
    element.click()
  }
}
